import cn from 'classnames';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import useSearch from '../../../hooks/useSearch';
import Icon from '../../../ui/Icon/Icon';
import { searchKeyMinLength } from '../../../utils/constants';

import { useAccessibility } from '../../../contexts/AccessibilityContext';
import { ThemeEnum } from '../../../utils/enums';
import './searchInput.scss';

const SearchInput = () => {
  const { redirectToSearchPage } = useSearch();
  const {
    settings: { theme },
  } = useAccessibility();
  const location = useLocation();

  const [key, setKey] = useState('');
  const [collapsed, setCollapsed] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const searchInputRef = useRef<HTMLDivElement>(null);

  const isKeyValid = useMemo(() => key.trim().length >= searchKeyMinLength, [key]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setKey(e.target.value);
  };

  const handleKeydown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && isKeyValid) {
      redirectToSearchPage(key);
    }
  };

  const handleSearchBtnClick = () => {
    if (!collapsed) {
      setCollapsed(true);
    } else if (isKeyValid) {
      redirectToSearchPage(key);
    }
  };

  useEffect(() => {
    setKey('');
    setCollapsed(false);
  }, [location.pathname]);

  useEffect(() => {
    const outsideClickHandler = (event: MouseEvent) => {
      if (!collapsed) return;

      if (searchInputRef.current && !searchInputRef.current.contains(event.target as Node)) {
        setKey('');
        setCollapsed(false);
      }
    };

    if (collapsed) {
      document.addEventListener('mousedown', outsideClickHandler);
    }

    return () => {
      document.removeEventListener('mousedown', outsideClickHandler);
    };
  }, [collapsed, key]);

  const iconClassName = useMemo(() => {
    const isDarkTheme = theme === ThemeEnum.Dark;
    const isLightTheme = theme === ThemeEnum.Light;

    return isDarkTheme
      ? 'loopDark'
      : collapsed && isKeyValid && !isLightTheme
        ? 'loopActive'
        : 'loop';
  }, [theme, collapsed, isKeyValid]);

  const searchInputClassName = cn('header-search-input-holder', { collapsed });

  return (
    <div className={searchInputClassName} ref={searchInputRef}>
      {collapsed && (
        <input
          autoFocus
          type='text'
          placeholder='Поиск по сайту'
          ref={inputRef}
          value={key}
          onChange={handleChange}
          onKeyDown={handleKeydown}
        />
      )}
      <button type='button' aria-label='Посмотреть результаты' onClick={handleSearchBtnClick}>
        <Icon name={iconClassName} size={24} className='search-icon' />
      </button>
    </div>
  );
};

export default SearchInput;
